<template>
  <product-costs
    :value="adaptedValue"
    :currency="currency"
    :kid-type="nonPureKidTypeSentinel"
    :is-mop="isMop"
    hide-maturity-costs
    :first-period-label="firstPeriodLabel"
  ></product-costs>
</template>

<script>
// "Profilo dei costi" (CostsProfile). Uses components/ProductDetails/ProductCosts.vue
// (prop-driven only, no Vuex/parent coupling) for its "Costi nel Tempo"
// table. That component also renders a
// SECOND, unrelated table ("Costi a scadenza") — this page renders that as its own separate
// CostsAtExpiration section instead, so `hide-maturity-costs` suppresses it here.
//
// ProductCosts.vue expects `value.period1/2/3 = { costsYear, costsMin: {riy, cost}, costsMax: {riy,
// cost} }` — not this page's CostsProfilePayload (`{ period1/2/3: { period, riy, totalCosts, riyMin,
// riyMed, riyMax, totalCostsMin, totalCostsMed, totalCostsMax } }`). `riyMed`/`totalCostsMed` are
// intentionally dropped here since ProductCosts.vue only ever shows Min/Max rows.
//
// `isMop` is inferred from whether ANY period carries a Min/Max value (MOP shape) vs. a plain
// Riy/TotalCosts value (SOP shape) — CostsProfileSectionBuilder always populates one pair or the
// other, never both, so this is exact, not a guess (checked across all 3 periods, not just period1,
// in case one period's own Min/Max happens to be entirely null while another's isn't).
import ProductCosts from '../../ProductDetails/ProductCosts'

export default {
  name: 'costs-profile-section',
  components: {
    ProductCosts,
  },
  inject: ['detailPage'],
  props: {
    payload: { type: Object, required: true },
  },
  computed: {
    currency() {
      return this.detailPage.currency
    },
    // ProductCosts.vue's own isPureMop() gate (kidType === 1) is already guaranteed false by the time
    // this component is even dispatched — CostsProfileSectionBuilder returns null (no section at all)
    // for a Pure-KID product, so DetailSection.vue never instantiates this. Any non-1 sentinel works;
    // there is no real kidType to pass through from CostsProfilePayload.
    nonPureKidTypeSentinel() {
      return 0
    },
    isMop() {
      const periods = [this.payload.period1, this.payload.period2, this.payload.period3]
      return periods.some(
        (p) => p.riyMin != null || p.riyMax != null || p.totalCostsMin != null || p.totalCostsMax != null
      )
    },
    firstPeriodLabel() {
      return this.payload.showFirstEarlyRedemptionDateLabel ? this.$t('productDetails.firstEarlyRedemptionDate') : null
    },
    adaptedValue() {
      return {
        period1: this.adaptPeriod(this.payload.period1, 1),
        period2: this.adaptPeriod(this.payload.period2, 2),
        period3: this.adaptPeriod(this.payload.period3, 3),
      }
    },
  },
  methods: {
    // Period3 is always "at RHP" by definition (the shared PRIIPs "1 year / half RHP / RHP" slot
    // convention, IC_SCENARIO_PERIODS_DESIGN.md's own opening paragraph) — ProductCosts.vue's own
    // periodsHeaders() infers "this column is RHP" from costsYear === 0, an IBIPs-only DOXA
    // convention (that slot was always populated as a literal 0 there). IC's own Period3 carries the
    // real RHP figure instead (e.g. 5.05 for a real product, verified against the DB) — passed
    // through unchanged, the header would show "5.05" instead of "RHP". Forced to 0 here whenever
    // period3 actually has data, preserving `null` when it doesn't so the column
    // still hides correctly — only this label signal changes, the riy/cost VALUES below read the
    // real payload untouched.
    adaptPeriod(period, periodNumber) {
      const costsYear = periodNumber === 3 && period.period != null ? 0 : period.period
      if (this.isMop) {
        return {
          costsYear,
          costsMin: { riy: period.riyMin, cost: period.totalCostsMin },
          costsMax: { riy: period.riyMax, cost: period.totalCostsMax },
        }
      }
      return {
        costsYear,
        costsMin: { riy: period.riy, cost: period.totalCosts },
        costsMax: { riy: period.riy, cost: period.totalCosts },
      }
    },
  },
}
</script>
